import React from 'react';
import { ReactComponent as Close } from 'assets/icons/close.svg';
import { ReactComponent as Arrow } from 'assets/icons/upArrow.svg';
import Select, {
  ClearIndicatorProps,
  components,
  DropdownIndicatorProps,
  GroupBase,
  Props as SelectProps,
  MultiValueGenericProps,
  MultiValueRemoveProps,
  PlaceholderProps,
  ActionMeta,
  MenuListProps,
} from 'react-select';
import {
  useController,
  FieldValues,
  useFormContext,
  Path,
} from 'react-hook-form';
import FieldError from './FieldError';
import { twJoin, twMerge } from 'tailwind-merge';
import FieldLabel from './FieldLabel';
import FieldDescribe from './FieldDescribe';
import { isArray } from 'lodash';
import Spinner from '../../Spinner';

export interface SelectOption {
  label: string;
  value: string;
}

export const DEFAULT_NONE_OPTION = 'None';

type ReactSelectProps = Omit<
  SelectProps<SelectOption, true, GroupBase<SelectOption>>,
  'name' | 'options' | 'value' | 'onChange' | 'defaultValue'
>;

export interface FormMultiSelectProps<TFieldValues extends FieldValues>
  extends ReactSelectProps {
  name: Path<TFieldValues>;
  label: string;
  describe?: React.ReactNode;
  isRequired?: boolean;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  noneOption?: string;
}

const ClearIndicator = (props: ClearIndicatorProps<SelectOption, true>) => {
  return (
    <components.ClearIndicator {...props}>
      <Close className="stroke-med-gray-3 h-5" />
    </components.ClearIndicator>
  );
};

const DropdownIndicator = (
  props: DropdownIndicatorProps<SelectOption, true>,
) => {
  return (
    <components.DropdownIndicator {...props}>
      <Arrow
        className={twJoin(
          'h-1.5 transition-transform',
          props.selectProps.menuIsOpen ? 'rotate-180' : 'rotate-0',
        )}
      />
    </components.DropdownIndicator>
  );
};

const MultiValueLabel = (props: MultiValueGenericProps<SelectOption>) => {
  return (
    <components.MultiValueLabel
      {...props}
      innerProps={{
        ...props.innerProps,
        className: 'text-sm px-2 py-1 text-med-gray-3 font-semibold',
      }}
    >
      {props.children}
    </components.MultiValueLabel>
  );
};

const MultiValueRemove = (props: MultiValueRemoveProps<SelectOption>) => {
  return (
    <components.MultiValueRemove {...props}>
      <Close className="fill-med-gray-3 w-4 h-4" />
    </components.MultiValueRemove>
  );
};

const Placeholder = (props: PlaceholderProps<SelectOption>) => {
  return (
    <components.Placeholder {...props}>
      <p className="text-med-gray-3 font-medium text-sm">{props.children}</p>
    </components.Placeholder>
  );
};

const CustomMenuList = (props: MenuListProps<SelectOption>) => {
  const { isLoading } = props.selectProps;

  return (
    <components.MenuList {...props}>
      {props.children}

      {isLoading && (
        <div className="flex justify-center items-center py-1.5">
          <Spinner />
        </div>
      )}
    </components.MenuList>
  );
};

const FormMultiSelect = <TFieldValues extends FieldValues>({
  name,
  label,
  options,
  placeholder,
  describe,
  isRequired,
  className,
  noneOption = DEFAULT_NONE_OPTION,
  ...props
}: FormMultiSelectProps<TFieldValues>) => {
  const { control } = useFormContext<TFieldValues>();
  const {
    field,
    fieldState: { error },
  } = useController({ name, control });

  const selectedValues = (isArray(field.value) ? field.value : [])
    .map((val: string) => options.find((option) => option.value === val))
    .filter(Boolean) as SelectOption[];

  const handleChange = (
    selectedOptions: readonly SelectOption[],
    actionMeta: ActionMeta<SelectOption>,
  ) => {
    if (actionMeta.option?.value === noneOption) {
      field.onChange([noneOption]);
      return;
    }

    const selected = Array.from(selectedOptions);

    field.onChange(
      selected
        .map((option) => option.value)
        .filter((opt) => opt !== noneOption),
    );
  };

  return (
    <div className={twMerge('flex flex-col gap-2', className)}>
      <FieldLabel name={name} label={label} isRequired={isRequired} />
      <Select
        {...field}
        {...props}
        id={name}
        isDisabled={props.disabled}
        isMulti
        options={options}
        placeholder={placeholder}
        value={selectedValues}
        onChange={handleChange}
        components={{
          MultiValueLabel,
          MultiValueRemove,
          ClearIndicator,
          DropdownIndicator,
          Placeholder,
          MenuList: CustomMenuList,
        }}
        className="dropdown"
        classNamePrefix={
          error && !props.disabled ? 'react-select-with-error' : 'react-select'
        }
      />
      {error && !props.disabled && <FieldError>{error.message}</FieldError>}
      {!error && describe && <FieldDescribe>{describe}</FieldDescribe>}
    </div>
  );
};

export default FormMultiSelect;
